import BaseAPIClient from './base/BaseAPIClient';
import ListResponseModel from '../models/ListResponseModel';

import UserModel from '../models/UserModel';
import CompanyModel from '../models/CompanyModel';
import SettingsModel from '../models/SettingsModel';
import CsvImportModel from '../models/CsvImportModel';
import JsonImportModel from '../models/JsonImportModel';
import OwnerDashboardSuggestionModel from '../models/OwnerDashboardSuggestionModel';
import OwnerSuggestionResponseModel from '../models/OwnerSuggestionResponseModel';
import TemplateLibraryMetricsModel from '../models/TemplateLibraryMetricsModel';

export default class AdminClient extends BaseAPIClient {
  getStats() {
    return this._get('helix_admin/stats/');
  }

  deletePendingInvite(id) {
    return this._delete(`helix_admin/company/invite/${id}/`);
  }

  getMetricsNewUsers() {
    return this._get('metrics/new_users/histogram/');
  }

  getMetricsUsers() {
    return this._get('metrics/users/');
  }

  getMetricsCompanies() {
    return this._get('metrics/companies/');
  }

  getMetricsTemplateLibrary() {
    return this._get('metrics/template_library/').then(m => new TemplateLibraryMetricsModel(m));
  }

  generateQRCodes(quantity, type) {
    return this._post('helix_admin/generate_qr_codes/', {
      quantity,
      type,
    });
  }

  getUser(email) {
    return this._get('helix_admin/users/get_by_email/', { email }).then(user => new UserModel(user));
  }

  getPendingCompanyUsers(search = '', ordering = '-id', format = '', limit = 50, offset = 0) {
    const params = {
      search,
      ordering,
      limit,
      offset,
    };

    if (format) {
      params.format = format;
    }

    const promise = this._get('helix_admin/pending_users_invites/', params);

    if (params.format) {
      return promise; // csv
    }

    return promise.then(m => new ListResponseModel(m, UserModel));
  }

  resendCompanyInvite(id) {
    return this._post(`helix_admin/company/invite/${id}/resend/`);
  }

  getSPCompanies(search = '', limit = 50, offset = 0) {
    const params = {
      search,
      ordering: 'name',
      is_service_provider: true,
      limit,
      offset,
    };

    return this._get('helix_admin/companies/', params).then(m => new ListResponseModel(m, CompanyModel));
  }

  getCompany(id) {
    return this._get(`helix_admin/companies/${id}/`).then(e => new CompanyModel(e));
  }

  getCompanies(search = '', ordering = 'created', format = '', limit = 50, offset = 0, filters = {}) {
    const params = {
      search,
      ordering,
      limit,
      offset,
      ...filters,
    };

    let url = 'helix_admin/companies/';

    if (format) {
      params.format = format;
      if (format == 'csv') {
        url = 'helix_admin/companies_csv/';
      }
    }

    const promise = this._get(url, params);

    if (params.format) {
      return promise; // csv
    }

    return promise.then(m => new ListResponseModel(m, CompanyModel));
  }

  editCompany(id, {
    name,
    email,
    street_address,
    street_address_line_2,
    latitude,
    longitude,
    city,
    state,
    zip,
    phone,
    phone_ext,

    is_equipment_operator,
    is_service_provider,
    service_provider_type,
    equipment_operator_type,

    helix_fee_percentage_one_time_service,
    helix_fee_percentage_service_agreement,
    helix_fee_percentage_parts_order,
    helix_fee_percentage_new_equipment,
    helix_fee_percentage_rental_equipment,
    helix_fee_percentage_energy_assessment,

    demo_seed_company,

    tax_exempt,
    tax_exempt_document,

    industry,
    industry_other,
    naic_code,
    how_did_you_hear_about_us,
    how_did_you_hear_about_us_explanation,
    affiliate,
    hours_of_operation,
    client_company_number,
    client_policy_number,
    affiliate_policy_number,
    description,
    salesforce_id,
    is_helix_test_company,
  }) {
    const data = {
      name,
      email,
      street_address,
      street_address_line_2,
      location: latitude || longitude ? {
        latitude,
        longitude,
      } : null,
      city,
      state,
      zip,
      phone: phone.replace(/[^0-9+]/g, ''),
      is_equipment_operator,
      is_service_provider,
      service_provider_type,
      equipment_operator_type,

      helix_fee_percentage_one_time_service,
      helix_fee_percentage_service_agreement,
      helix_fee_percentage_parts_order,
      helix_fee_percentage_new_equipment,
      helix_fee_percentage_rental_equipment,
      helix_fee_percentage_energy_assessment,

      demo_seed_company,

      industry,
      industry_other,
      naic_code: naic_code || null,
      how_did_you_hear_about_us,
      how_did_you_hear_about_us_explanation,

      tax_exempt,
      affiliate,
      client_company_number,
      client_policy_number,
      affiliate_policy_number,
      description,
      salesforce_id,
      is_helix_test_company,
    };

    if (phone_ext) {
      data.phone_ext = phone_ext;
    }

    if (hours_of_operation) {
      data.hours_of_operation = hours_of_operation;
    }

    if (tax_exempt_document) {
      data.tax_exempt_document = tax_exempt_document;
    }

    return this._patch(`helix_admin/companies/${id}/`, data).then(e => new CompanyModel(e));
  }

  getAppUsers(search = '', ordering = 'last_login', format = '', limit = 50, offset = 0, filters = {}) {
    const params = {
      search,
      ordering,
      limit,
      offset,
      ...filters,
    };

    let url = 'helix_admin/users/';

    if (format) {
      params.format = format;
      if (format == 'csv') {
        url = 'helix_admin/users_csv/';
      }
    }

    const promise = this._get(url, params);

    if (params.format) {
      return promise; // csv
    }

    return promise.then(m => new ListResponseModel(m, UserModel));
  }

  inviteAdminUser(email, role, first_name, last_name) {
    return this._post('helix_admin/users/', {
      email,
      role,
      first_name,
      last_name,
    });
  }

  inviteCompanyUser(company_id, role, email, first_name, last_name) {
    return this._post(`company/${company_id}/invites/`, {
      email,
      role,
      first_name,
      last_name,
    });
  }

  getAdminUserRoles() {
    return this._get('helix_admin/user_roles/');
  }

  setUserRoles(id, roles) {
    return this._put(`helix_admin/users/${id}/roles/`, {
      roles, // ['',...]
    });
  }

  editUser(id, first_name, last_name) {
    return this._post(`helix_admin/users/${id}/`, {
      first_name,
      last_name,
    });
  }

  updateUser(id, updates) {
    return this._patch(`helix_admin/users/${id}/`, updates);
  }

  getGlobalSettings() {
    return this._get('dynamic_config_variables/').then(m => new ListResponseModel(m, SettingsModel));
  }

  setGlobalSetting(id, boolean_value) {
    return this._patch(`dynamic_config_variables/${id}/`, {
      boolean_value,
    });
  }

  async getJsonTemplate() {
    const s = await this._get('helix_admin/json_import/schema/?format=json');
    const blob = new Blob([JSON.stringify(s, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    return url;
  }

  getJsonImport(id) {
    return this._get(`helix_admin/json_import/${id}/`).then(m => new JsonImportModel(m));
  }

  uploadJson(file) {
    return this._post('helix_admin/json_import/', {
      file,
    }).then(m => new JsonImportModel(m));
  }

  getCsvImport(id) {
    return this._get(`helix_admin/csv_import/${id}/`).then(m => new CsvImportModel(m));
  }

  uploadCompanyCSV(file) {
    return this._post('helix_admin/companies_csv_import/', {
      file,
    }).then(m => new CsvImportModel(m));
  }

  uploadCompanyMembersCSV(companyId, file) {
    const url = companyId ? `helix_admin/companies/${companyId}/members_csv_import/` : 'helix_admin/companies/members_csv_import/';

    return this._post(url, {
      file,
    }).then(m => new CsvImportModel(m));
  }

  uploadEquipmentsCSV(companyId, file) {
    const url = companyId ? `helix_admin/companies/${companyId}/equipments_csv_import/` : 'helix_admin/companies/equipments_csv_import/';

    return this._post(url, {
      file,
    }).then(m => new CsvImportModel(m));
  }

  uploadPartsCSV(companyId, file) {
    return this._post(`helix_admin/companies/${companyId}/parts_csv_import/`, {
      file,
    }).then(m => new CsvImportModel(m));
  }

  uploadTasksCSV(companyId, file) {
    return this._post(`helix_admin/companies/${companyId}/tasks_csv_import/`, {
      file,
    }).then(m => new CsvImportModel(m));
  }

  uploadLocationsCSV(companyId, file) {
    const url = companyId ? `helix_admin/companies/${companyId}/location_csv_import/` : 'helix_admin/companies/location_csv_import/';

    return this._post(url, {
      file,
    }).then(m => new CsvImportModel(m));
  }

  createCompany(data) {
    return this._post('helix_admin/companies/', data).then(d => new CompanyModel(d));
  }

  inviteNewCompanyAdmin({
    name,
    email,
    street_address,
    latitude,
    longitude,
    city,
    state,
    zip,
    phone,
    phone_ext,
    is_equipment_operator,
    is_service_provider,
    service_provider_type,
    equipment_operator_type,

    helix_fee_percentage_one_time_service,
    helix_fee_percentage_service_agreement,
    helix_fee_percentage_parts_order,
    helix_fee_percentage_new_equipment,
    helix_fee_percentage_rental_equipment,
    helix_fee_percentage_energy_assessment,

    industry,
    industry_other,
    naic_code,
    how_did_you_hear_about_us,
    how_did_you_hear_about_us_explanation,

    admin_email,
    admin_first_name,
    admin_last_name,
    admin_phone,
    admin_phone_ext,
    demo_seed_company_id,
    parent,

    affiliate,
    related_company_id,
    capabilities,
  }) {
    const data = {
      name,
      email,
      street_address,
      location: latitude || longitude ? {
        latitude,
        longitude,
      } : null,
      city,
      state,
      zip,
      phone: phone.replace(/[^0-9+]/g, ''),
      is_equipment_operator,
      is_service_provider,
      service_provider_type,
      equipment_operator_type,
      helix_fee_percentage_one_time_service,
      helix_fee_percentage_service_agreement,
      helix_fee_percentage_parts_order,
      helix_fee_percentage_new_equipment,
      helix_fee_percentage_rental_equipment,
      helix_fee_percentage_energy_assessment,

      industry,
      industry_other,
      naic_code: naic_code || null,
      how_did_you_hear_about_us,
      how_did_you_hear_about_us_explanation,

      admin_user_invitation: {
        email: admin_email,
        first_name: admin_first_name,
        last_name: admin_last_name,
      },
    };

    if (demo_seed_company_id) {
      data.demo_seed_company_id = demo_seed_company_id;
    }

    if (parent) {
      data.parent = parent;
    }

    if (phone_ext) {
      data.phone_ext = phone_ext;
    }

    if (affiliate) {
      data.affiliate = affiliate;
    }

    if (related_company_id) {
      data.related_company_id = related_company_id;
    }

    if (capabilities && Object.keys(capabilities).length) {
      data.capabilities = capabilities;
    }

    if (admin_phone) {
      data.admin_user_invitation.phone = admin_phone.replace(/[^0-9+]/g, '');
    }

    if (admin_phone_ext) {
      data.admin_user_invitation.phone_ext = admin_phone_ext;
    }

    return this._post('helix_admin/companies/admin_invitations/', data).then(d => new CompanyModel(d));
  }

  getSuggestionStats(active, limit = 1, offset = 0, ordering = '-id') {
    return this._get('helix_admin/suggestions/dashboard/stats/', {
      offset,
      limit,
      active,
      ordering,
    }).then(d => new ListResponseModel(d, OwnerDashboardSuggestionModel));
  }

  getSuggestionResponses(id, limit = 1, offset = 0) {
    return this._get(`helix_admin/suggestions/dashboard/${id}/responses/`, {
      offset,
      limit,
    }).then(d => new ListResponseModel(d, OwnerSuggestionResponseModel));
  }

  getCompanyCapabilityDetail(capabilityName) {
    return this._get(`helix_admin/company/capabilities/${capabilityName}/`).then(d => d);
  }
}
